import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

// Column statistics from the grid
export interface ColumnStats {
  [columnName: string]: {
    min: number;
    max: number;
    isNumeric: boolean;
  };
}

// Per-column heatmap configuration
export interface HeatmapColumnConfig {
  enabled: boolean;
  min: number;
  max: number;
}

export interface HeatmapSettings {
  intensity: number;
  columns: {
    [columnName: string]: HeatmapColumnConfig;
  };
}

interface HeatmapPanelProps {
  isOpen: boolean;
  onClose: () => void;
  heatmapSettings: HeatmapSettings;
  onHeatmapSettingsChange: (settings: HeatmapSettings) => void;
  columnStats: ColumnStats;
}

const HeatmapPanel: React.FC<HeatmapPanelProps> = ({
  isOpen,
  onClose,
  heatmapSettings,
  onHeatmapSettingsChange,
  columnStats,
}) => {
  // Position state for dragging
  const [position, setPosition] = useState({ x: window.innerWidth - 340, y: 100 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const panelRef = useRef<HTMLDivElement>(null);

  // Handle drag start
  const handleMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).closest('input, button, label')) return;
    setIsDragging(true);
    setDragOffset({
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    });
  }, [position]);

  // Handle drag move
  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      const newX = Math.max(0, Math.min(window.innerWidth - 320, e.clientX - dragOffset.x));
      const newY = Math.max(0, Math.min(window.innerHeight - 100, e.clientY - dragOffset.y));
      setPosition({ x: newX, y: newY });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragOffset]);

  // Handle Escape key to close
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const numericColumns = Object.entries(columnStats).filter(([_, stat]) => stat.isNumeric);

  return createPortal(
    <div
      ref={panelRef}
      style={{
        position: 'fixed',
        left: position.x,
        top: position.y,
        zIndex: 9998,
        cursor: isDragging ? 'grabbing' : 'default',
      }}
      className="w-80 bg-white rounded-lg shadow-2xl border border-gray-200 select-none"
    >
      {/* Header - Draggable */}
      <div
        onMouseDown={handleMouseDown}
        className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-t-lg cursor-grab active:cursor-grabbing"
      >
        <div className="flex items-center gap-2">
          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          <span className="text-white font-semibold text-sm">Heatmap Settings</span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => {
              const w = window.open(
                '/popout/heatmap',
                'heatmap-popout',
                'width=380,height=700,menubar=no,toolbar=no,location=no,status=no,scrollbars=yes,resizable=yes'
              );
              if (w) w.focus();
            }}
            className="text-white/80 hover:text-white transition-colors p-1 rounded hover:bg-white/20"
            title="Pop out to separate window"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </button>
          <button
            onClick={onClose}
            className="text-white/80 hover:text-white transition-colors p-1 rounded hover:bg-white/20"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 max-h-[60vh] overflow-y-auto">
        {/* Global Intensity Slider */}
        <div className="mb-4 pb-4 border-b border-gray-200">
          <div className="flex justify-between text-sm text-gray-700 mb-2">
            <span className="font-medium">Color Intensity</span>
            <span className="text-emerald-600 font-semibold">{Math.round(heatmapSettings.intensity * 100)}%</span>
          </div>
          <input
            type="range"
            min="10"
            max="100"
            value={heatmapSettings.intensity * 100}
            onChange={(e) => onHeatmapSettingsChange({
              ...heatmapSettings,
              intensity: parseInt(e.target.value) / 100
            })}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-emerald-500"
          />
          <div className="flex justify-between text-xs text-gray-400 mt-1">
            <span>Subtle</span>
            <span>Vibrant</span>
          </div>
        </div>

        {/* Per-column settings */}
        {numericColumns.length > 0 ? (
          <div>
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-gray-700">Column Thresholds</span>
              <button
                onClick={() => {
                  // Enable all columns with their data ranges
                  const newColumns: { [key: string]: HeatmapColumnConfig } = {};
                  numericColumns.forEach(([colName, stat]) => {
                    newColumns[colName] = { enabled: true, min: stat.min, max: stat.max };
                  });
                  onHeatmapSettingsChange({ ...heatmapSettings, columns: newColumns });
                }}
                className="text-xs text-emerald-600 hover:text-emerald-700"
              >
                Enable All
              </button>
            </div>

            <div className="space-y-3">
              {numericColumns.map(([colName, stat]) => {
                const config = heatmapSettings.columns[colName] || {
                  enabled: true,
                  min: stat.min,
                  max: stat.max
                };
                const step = (stat.max - stat.min) / 100 || 0.01;
                const precision = stat.max > 100 ? 0 : stat.max > 10 ? 1 : 2;

                return (
                  <div
                    key={colName}
                    className={`p-3 rounded-lg border transition-colors ${
                      config.enabled ? 'bg-emerald-50 border-emerald-200' : 'bg-gray-50 border-gray-200'
                    }`}
                  >
                    <label className="flex items-center gap-2 cursor-pointer mb-2">
                      <input
                        type="checkbox"
                        checked={config.enabled}
                        onChange={(e) => onHeatmapSettingsChange({
                          ...heatmapSettings,
                          columns: {
                            ...heatmapSettings.columns,
                            [colName]: { ...config, enabled: e.target.checked }
                          }
                        })}
                        className="rounded border-gray-300 text-emerald-500 focus:ring-emerald-500"
                      />
                      <span className={`text-sm font-medium truncate ${config.enabled ? 'text-gray-900' : 'text-gray-500'}`} title={colName}>
                        {colName}
                      </span>
                    </label>

                    {config.enabled && (
                      <div className="space-y-2 pl-4 pr-1">
                        {/* Min slider */}
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500 w-7 shrink-0">Min</span>
                          <input
                            type="range"
                            min={stat.min}
                            max={config.max - step}
                            step={step}
                            value={config.min}
                            onChange={(e) => onHeatmapSettingsChange({
                              ...heatmapSettings,
                              columns: {
                                ...heatmapSettings.columns,
                                [colName]: { ...config, min: parseFloat(e.target.value) }
                              }
                            })}
                            className="flex-1 min-w-0 h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-emerald-500"
                          />
                          <span className="text-xs font-mono text-gray-700 w-12 shrink-0 text-right">
                            {config.min.toFixed(precision)}
                          </span>
                        </div>

                        {/* Max slider */}
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500 w-7 shrink-0">Max</span>
                          <input
                            type="range"
                            min={config.min + step}
                            max={stat.max}
                            step={step}
                            value={config.max}
                            onChange={(e) => onHeatmapSettingsChange({
                              ...heatmapSettings,
                              columns: {
                                ...heatmapSettings.columns,
                                [colName]: { ...config, max: parseFloat(e.target.value) }
                              }
                            })}
                            className="flex-1 min-w-0 h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-emerald-500"
                          />
                          <span className="text-xs font-mono text-gray-700 w-12 shrink-0 text-right">
                            {config.max.toFixed(precision)}
                          </span>
                        </div>

                        {/* Data range info */}
                        <div className="text-[10px] text-gray-400 flex justify-between">
                          <span>Data: {stat.min.toFixed(precision)}</span>
                          <span>to {stat.max.toFixed(precision)}</span>
                        </div>

                        {/* Reset to data range */}
                        <button
                          onClick={() => onHeatmapSettingsChange({
                            ...heatmapSettings,
                            columns: {
                              ...heatmapSettings.columns,
                              [colName]: { enabled: true, min: stat.min, max: stat.max }
                            }
                          })}
                          className="text-[10px] text-emerald-600 hover:text-emerald-700"
                        >
                          Reset to data range
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <svg className="w-12 h-12 mx-auto text-gray-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            <p className="text-sm">No numeric columns found</p>
            <p className="text-xs text-gray-400 mt-1">Load data to configure heatmap</p>
          </div>
        )}

        {/* Reset All button */}
        {numericColumns.length > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <button
              onClick={() => onHeatmapSettingsChange({ intensity: 0.4, columns: {} })}
              className="w-full py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded transition-colors"
            >
              Reset All to Defaults
            </button>
          </div>
        )}
      </div>

      {/* Footer with drag hint */}
      <div className="px-4 py-2 bg-gray-50 rounded-b-lg border-t border-gray-100">
        <p className="text-[10px] text-gray-400 text-center">Drag header to move • Esc to close</p>
      </div>
    </div>,
    document.body
  );
};

export default HeatmapPanel;
